import { useApp, DEFAULT_SETTINGS } from "@/contexts/AppContext";
import Topbar from "@/components/layout/Topbar";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export default function Settings() {
  const { settings, setSettings } = useApp();
  const update = (k, v) => setSettings((s) => ({ ...s, [k]: v }));

  return (
    <>
      <Topbar title="Settings" subtitle="Adjust forecasting assumptions — changes apply instantly." />
      <div className="flex-1 px-8 py-7">
        <div className="max-w-3xl space-y-5">
          <Card title="Criticality & Safety Stock">
            <Row label="Default criticality (if missing)">
              <Select value={settings.defaultCriticality} onValueChange={(v) => update("defaultCriticality", v)}>
                <SelectTrigger data-testid="setting-default-criticality" className="h-9 w-40"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="High">High</SelectItem>
                  <SelectItem value="Medium">Medium</SelectItem>
                  <SelectItem value="Low">Low</SelectItem>
                </SelectContent>
              </Select>
            </Row>
            <SliderRow label="High criticality factor" testid="setting-safety-high" value={settings.safetyHigh} onChange={(v) => update("safetyHigh", v)} min={0.1} max={1} step={0.05} format={(v) => `${(v * 100).toFixed(0)}% of avg monthly`} />
            <SliderRow label="Medium criticality factor" testid="setting-safety-medium" value={settings.safetyMedium} onChange={(v) => update("safetyMedium", v)} min={0.05} max={0.8} step={0.05} format={(v) => `${(v * 100).toFixed(0)}% of avg monthly`} />
            <SliderRow label="Low criticality factor" testid="setting-safety-low" value={settings.safetyLow} onChange={(v) => update("safetyLow", v)} min={0} max={0.5} step={0.05} format={(v) => `${(v * 100).toFixed(0)}% of avg monthly`} />
          </Card>

          <Card title="Forecast Horizon & Thresholds">
            <Row label="Forecast horizon">
              <Select value={String(settings.forecastHorizon)} onValueChange={(v) => update("forecastHorizon", Number(v))}>
                <SelectTrigger data-testid="setting-horizon" className="h-9 w-40"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="30">30 days</SelectItem>
                  <SelectItem value="60">60 days</SelectItem>
                  <SelectItem value="90">90 days</SelectItem>
                </SelectContent>
              </Select>
            </Row>
            <SliderRow label="Slow-moving threshold" testid="setting-slow-days" value={settings.slowMovingDays} onChange={(v) => update("slowMovingDays", v)} min={30} max={365} step={15} format={(v) => `${v} days`} />
          </Card>

          <Card title="Display">
            <Row label="Currency symbol">
              <Input
                data-testid="setting-currency"
                className="h-9 w-24 font-mono"
                value={settings.currency}
                onChange={(e) => update("currency", e.target.value.slice(0, 3))}
              />
            </Row>
          </Card>

          <div className="flex justify-end gap-3">
            <Button
              data-testid="reset-settings-btn"
              variant="outline"
              onClick={() => { setSettings(DEFAULT_SETTINGS); toast.success("Settings reset to defaults."); }}
            >
              Reset to defaults
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}

function Card({ title, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg">
      <div className="px-5 py-4 border-b border-slate-200">
        <h3 className="font-heading font-semibold text-slate-900">{title}</h3>
      </div>
      <div className="p-5 space-y-5">{children}</div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex items-center justify-between gap-6">
      <Label className="text-sm text-slate-700">{label}</Label>
      <div>{children}</div>
    </div>
  );
}

function SliderRow({ label, value, onChange, min, max, step, format, testid }) {
  return (
    <div className="flex items-center justify-between gap-6">
      <Label className="text-sm text-slate-700 shrink-0 w-56">{label}</Label>
      <div className="flex items-center gap-4 flex-1 max-w-md">
        <Slider
          data-testid={testid}
          min={min}
          max={max}
          step={step}
          value={[value]}
          onValueChange={(v) => onChange(v[0])}
          className="flex-1"
        />
        <span className="font-mono text-xs text-slate-700 w-28 text-right">{format(value)}</span>
      </div>
    </div>
  );
}
